import React, { createContext, useContext, useEffect, useState } from "react";
import supabase from "./supabase";

const MasterDataContext = createContext({
  countries: [],
  ranks: [],
  extantFossil: [],
  loading: true,
  error: null,
  refresh: async () => {},
});

export function MasterDataProvider({ children }) {
  const [countries, setCountries] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [extantFossil, setExtantFossil] = useState([]);
  const [journals, setJournals] = useState([]);
  const [typeCategories, setTypeCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data: cData, error: cErr }, { data: rData, error: rErr }, { data: eData, error: eErr }] =
        await Promise.all([
          supabase
            .from("countries")
            .select("id, geojson_name, name")
            .order("geojson_name", { ascending: true }),
          supabase.from("rank").select("id").order("id", { ascending: true }),
          supabase.from("extant_fossil").select("id").order("id", { ascending: true }),
        ]);

      if (cErr || rErr || eErr) throw cErr || rErr || eErr;

      const fetchTypeCategories = async () => {
        const { data, error } = await supabase.from("type_categories").select("*");
        if (error) {
          console.error("[MasterData] type_categories fetch error", error);
          return [];
        }
        return (data || []).map((row) => {
          const label =
            row.name ??
            row.category ??
            row.type ??
            row.label ??
            row.title ??
            row.id ??
            row.code ??
            "";
          const id = row.id ?? row.code ?? label;
          return { id, name: label };
        });
      };

      const fetchJournals = async () => {
        // name_eng/name_org のみ（DBに name_english が無い環境で 400 を避ける）
        const { data, error } = await supabase
          .from("journals")
          .select("id, name_eng, name_org")
          .order("name_eng", { ascending: true });
        if (error) {
          console.error("[MasterData] journals fetch error", error);
          return [];
        }
        const mapped = (data || []).map((j) => ({
          id: j.id,
          name_english: j.name_eng ?? j.name_org ?? "",
          name_original: j.name_org ?? j.name_eng ?? "",
        }));
        console.log("[MasterData] journals loaded", mapped.length);
        return mapped;
      };

      const [tData, jData] = await Promise.all([fetchTypeCategories(), fetchJournals()]);

      console.log("[MasterData] loaded", {
        countries: cData?.length,
        ranks: rData?.length,
        extant: eData?.length,
        typeCategories: tData?.length,
        journals: jData?.length,
      });

      setCountries(cData || []);
      setRanks(rData || []);
      setExtantFossil(eData || []);
      setTypeCategories(tData || []);
      setJournals(jData || []);
    } catch (err) {
      setError(err?.message || "Failed to load master data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <MasterDataContext.Provider value={{ countries, ranks, extantFossil, typeCategories, journals, loading, error, refresh: load }}>
      {children}
    </MasterDataContext.Provider>
  );
}

export function useMasterData() {
  return useContext(MasterDataContext);
}
